import {
    getFilterStorageKey,
    readStoredFilter,
} from "containers/ExecutionFilter/filterStorage";
import { getValuesFromQuery, useSetQueryString } from "containers/ExecutionFilter/utils";
import { History } from "history";
import { useCallback, useMemo } from "react";
import { advancedSearchPathFragment } from "utils/RouterManager/constants";
import { parseSearchParams } from "utils/RouterManager/searchParams";
import { mapScreensToPaths } from "utils/RouterManager/utils";
import { initialAdvancedFilter, initialFilter, initialTab } from "./constants";
import { AdvancedSearchTabsType } from "./interfaces";

export const useAdvancedFilter = (queryParams: { [key: string]: string }, history: History) => {
    const filterStorageKey = getFilterStorageKey({ page: "advanced-search" });
    // The header search bar mounts this hook on every route. Only the advanced-search screen
    // owns `filter=` URL params; elsewhere they belong to the screen the URL points at.
    const isAdvancedSearchRoute = advancedSearchPathFragment in parseSearchParams(history.location.search);

    const {
        setQueryString,
        setInitialQueryString,
        setCustomFilter,
        effectiveQueryParams,
        hasStoredFilter,
    } = useSetQueryString(queryParams, history, filterStorageKey, false, isAdvancedSearchRoute);

    const {
        findBooleanValueInQuery,
        findStringValueInQuery,
        findValueInFilterQuery,
        findEncodedValueInFilterQuery,
    } = useMemo(() => getValuesFromQuery(effectiveQueryParams), [effectiveQueryParams]);

    const filteredValues = useMemo(
        () => ({
            name: findEncodedValueInFilterQuery("name"),
            resource_type: findValueInFilterQuery("resource_type").array,
            include_deleted: findBooleanValueInQuery("include_deleted", false),
            hide_deleted: findBooleanValueInQuery("hide_deleted", true),
            tab: (() => {
                const tab = findStringValueInQuery("tab", initialTab);
                return tab === "chats" ? initialTab : (tab as AdvancedSearchTabsType);
            })(),
        }),
        [
            findEncodedValueInFilterQuery,
            findValueInFilterQuery,
            findBooleanValueInQuery,
            findStringValueInQuery,
        ]
    );

    const navigateToAdvancedSearch = useCallback(
        (historyMethod: "push" | "replace" = "push") => {
            const target = mapScreensToPaths.advancedSearch;
            const current = `${history.location.pathname}${history.location.search}`;
            if (current !== target) {
                history[historyMethod](target);
            }
        },
        [history]
    );

    const setInitialQueryStringWithNavigation = useCallback(
        (
            initialFilterValues,
            historyMethod = "push",
            path = mapScreensToPaths.advancedSearch,
            additionalParams: { [key: string]: string } = {}
        ) => {
            const tab =
                additionalParams.tab === "chats"
                    ? initialTab
                    : ((additionalParams.tab as AdvancedSearchTabsType) ?? initialTab);
            setQueryString({
                ...initialFilterValues,
                tab,
                include_deleted: initialFilterValues.include_deleted ?? false,
                hide_deleted: initialFilterValues.hide_deleted ?? true,
            });
            const current = `${history.location.pathname}${history.location.search}`;
            if (current !== path) {
                history[historyMethod](path);
            }
        },
        [history, setQueryString]
    );

    const onSearchQueryChange = useCallback(
        (value: string) => {
            setQueryString({ ...initialFilter, name: value, tab: filteredValues.tab });
        },
        [filteredValues.tab, setQueryString]
    );

    const onResourceTypeChange = useCallback(
        (value: string) => {
            setQueryString({
                ...initialFilter,
                resource_type: [value],
                tab: filteredValues.tab,
            });
        },
        [filteredValues.tab, setQueryString]
    );

    const setInitialFilterHandler = useCallback(
        (historyMethod?: "push" | "replace") => {
            const storedTab = readStoredFilter(filterStorageKey)?.tab;
            const activeTab =
                storedTab === "chats"
                    ? initialTab
                    : ((storedTab as AdvancedSearchTabsType) ?? initialTab);

            if (!hasStoredFilter) {
                setInitialQueryString(initialAdvancedFilter[activeTab], historyMethod, {
                    tab: activeTab,
                });
            }
            navigateToAdvancedSearch(historyMethod);
        },
        [
            filterStorageKey,
            hasStoredFilter,
            navigateToAdvancedSearch,
            setInitialQueryString,
        ]
    );

    const seedAdvancedSearchFilter = useCallback(
        (
            filterValues: Record<string, unknown>,
            tab: AdvancedSearchTabsType = initialTab,
            historyMethod: "push" | "replace" = "push"
        ) => {
            setQueryString({
                ...filterValues,
                tab,
                include_deleted: filterValues.include_deleted ?? false,
                hide_deleted: filterValues.hide_deleted ?? true,
            });
            navigateToAdvancedSearch(historyMethod);
        },
        [navigateToAdvancedSearch, setQueryString]
    );

    return {
        filteredValues,
        effectiveQueryParams,
        hasStoredFilter,
        onChangeHandlers: {
            onSearchQueryChange,
            setInitialFilterHandler,
            onResourceTypeChange,
        },
        setInitialQueryString: setInitialQueryStringWithNavigation,
        setQueryString,
        setCustomFilter,
        seedAdvancedSearchFilter,
    };
};
